import { useState, useEffect, forwardRef, useContext } from "react";
import { Dialog } from "@mui/material";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid,
  Paper,
  Switch,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EventContext } from "../../../contexts/EventContext";
import { toast } from "../../../utilities/SnackBarUtils";
import request from "../../../utilities/requests";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Contract = ({ open, close, org }) => {
  const { userData } = useContext(EventContext);
  const [disabled, setDisabled] = useState(true);
  const [contract, setContract] = useState(null);
  const [form, setForm] = useState(null);
  const [newContract, setNewContract] = useState(false);
  const [ticketFees, setTicketFees] = useState(null);
  const [smsFees, setSmsFees] = useState(null);

  // When contract is loaded, populate form state
  useEffect(() => {
    if (contract) {
      setForm({
        eventRates: contract.eventRates,
        billingCycle: contract.billingCycle,
        resetPeriod: contract.resetPeriod,
        contractType: contract.contractType,
        billingContacts: contract.billingContacts || [],
        _id: contract._id,
      });
    }
  }, [contract]);

  useEffect(() => {
    if (userData?.role === "superadmin") setDisabled(false);
  }, [userData]);

  const handleFieldChange = (path, value) => {
    const keys = path.split(".");
    setForm((prev) => {
      const newForm = { ...prev };
      let cur = newForm;
      // Traverse and clone intermediate objects/arrays
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (Array.isArray(cur[key])) {
          cur[key] = [...cur[key]];
        } else if (cur[key] && typeof cur[key] === "object") {
          cur[key] = { ...cur[key] };
        }
        cur = cur[key];
      }
      const lastKey = keys[keys.length - 1];
      cur[lastKey] = value;
      return newForm;
    });
  };

  const handleFeeChange = (field, value) => {
    setTicketFees((prev) => ({ ...(prev || {}), [field]: value }));
  };
  const handleSmsChange = (field, value) => {
    setSmsFees((prev) => ({ ...(prev || {}), [field]: value }));
  };

  useEffect(() => {
    if (open && org) getContract();
  }, [open]);

  const getContract = async () => {
    const result = await request(`contracts/contract?orgId=${org._id}`);
    if (result.success) {
      setNewContract(false);
      setContract(result.data.contract);
      setTicketFees({
        percent: result?.ticketFees?.percent ?? 0,
        fixedFeeCents: result?.ticketFees?.fixedFeeCents ?? 0,
        processingPercent: result?.ticketFees?.processingPercent ?? 2.9,
        processingFixed: result?.ticketFees?.processingFixed ?? 0.3,
        currency: result?.ticketFees?.currency ?? "USD",
      });
      setSmsFees({
        enabled: result?.data?.contract?.sms?.enabled ?? false,
        monthlyRate: result?.data?.contract?.sms?.monthlyRate ?? 0,
        includedMessages: result?.data?.contract?.sms?.includedMessages ?? 0,
        perMessageFee: result?.data?.contract?.sms?.perMessageFee ?? 0,
        startService: result?.data?.contract?.sms?.startService ?? null,
      });
    } else if (result.noContract) {
      setNewContract(true);
      setContract({
        eventRates: [
          { eventType: "speeddating", basePrice: 5, volumeDiscount: null },
          { eventType: "mixer", basePrice: 2, volumeDiscount: null },
          { eventType: "friend", basePrice: 3, volumeDiscount: null },
          { eventType: "group", basePrice: 3, volumeDiscount: null },
        ],
        billingCycle: { type: "per_event", dueDays: 5 },
        contractType: "invoiced",
        resetPeriod: "monthly",
        ticketFees: {
          percent: 0,
          fixedFeeCents: 0,
          processingPercent: 2.9,
          processingFixed: 0.3,
          currency: "USD",
        },
      });
      setSmsFees({
        enabled: false,
        monthlyRate: 0,
        includedMessages: 0,
        perMessageFee: 0,
        startService: null,
      });
    }
  };

  const handleClose = () => {
    setContract(null);
    setNewContract(false);
    close();
  };

  const handleSave = async () => {
    try {
      // Build payload from form data
      const body = {
        contractData: {
          eventRates: form.eventRates,
          billingCycle: form.billingCycle,
          resetPeriod: form.resetPeriod,
          contractType: form.contractType,
          billingContacts: form.billingContacts,
          sms: smsFees
        },
        ticketFees
      };

      if (newContract) {
        body.org = org._id;
        const result = await request(`contracts/contract/create`, "post", body);
        if (result.success) {
          toast.success("Contract saved");
        }
      } else {
        const result = await request(
          `contracts/contract/update/${form._id}`,
          "post",
          body
        );
        if (result.success) {
          toast.success("Contract saved");
        }
      }
      handleClose();
    } catch (err) {
      console.error("Error saving contract:", err);
    }
  };

  // Add/Remove Billing Contact handlers
  const handleAddBillingContact = () => {
    setForm((prev) => ({
      ...prev,
      billingContacts: [...(prev.billingContacts || []), ""],
    }));
  };

  const handleRemoveBillingContact = (index) => {
    setForm((prev) => {
      const updated = [...(prev.billingContacts || [])];
      updated.splice(index, 1);
      return { ...prev, billingContacts: updated };
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullScreen
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            {newContract ? "Create Contract" : "Edit Contract Terms"}
          </Typography>
          <Button
            autoFocus
            color="inherit"
            onClick={handleSave}
            disabled={disabled}
          >
            save
          </Button>
        </Toolbar>
      </AppBar>
      <DialogTitle>
        {newContract
          ? "No contract exists for this org. Create one now."
          : `${org?.name}`}
      </DialogTitle>
      <DialogContent>
        {/* Billing Contacts Section */}
        {form?.billingContacts && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: "#F0F0F5" }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  Billing Contacts
                </Typography>
              </Grid>
              {form.billingContacts.map((email, idx) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={idx}
                  sx={{ display: "flex", gap: 1 }}
                >
                  <TextField
                    disabled={disabled}
                    label={`Email ${idx + 1}`}
                    type="email"
                    fullWidth
                    value={email}
                    onChange={(e) =>
                      handleFieldChange(
                        `billingContacts.${idx}`,
                        e.target.value
                      )
                    }
                    margin="dense"
                  />
                  {!disabled && (
                    <Button
                      color="error"
                      onClick={() => handleRemoveBillingContact(idx)}
                    >
                      Remove
                    </Button>
                  )}
                </Grid>
              ))}
              {!disabled && (
                <Grid item xs={12}>
                  <Button variant="outlined" onClick={handleAddBillingContact}>
                    Add Contact
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
        {form ? (
          <Grid container spacing={2}>
            {/* Event Rates */}
            {form.eventRates.map((rate, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Paper sx={{ p: 2, mb: 2, backgroundColor: "#F0F0F5" }}>
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: "bold" }}
                  >
                    {rate.eventType}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={disabled}
                        checked={Boolean(rate.volumeDiscount)}
                        onChange={(e) =>
                          handleFieldChange(
                            `eventRates.${idx}.volumeDiscount`,
                            e.target.checked
                              ? { threshold: 0, discountedPrice: 0 }
                              : null
                          )
                        }
                      />
                    }
                    label="Volume Discount"
                  />
                  <TextField
                    disabled={disabled}
                    label="Base Price"
                    type="number"
                    fullWidth
                    value={rate.basePrice}
                    onChange={(e) =>
                      handleFieldChange(
                        `eventRates.${idx}.basePrice`,
                        Number(e.target.value)
                      )
                    }
                    margin="dense"
                  />
                  {rate.volumeDiscount && (
                    <>
                      <TextField
                        disabled={disabled}
                        label="Threshold"
                        type="number"
                        fullWidth
                        value={rate.volumeDiscount.threshold}
                        onChange={(e) =>
                          handleFieldChange(
                            `eventRates.${idx}.volumeDiscount.threshold`,
                            Number(e.target.value)
                          )
                        }
                        margin="dense"
                      />
                      <TextField
                        disabled={disabled}
                        label="Discounted Price"
                        type="number"
                        fullWidth
                        value={rate.volumeDiscount.discountedPrice}
                        onChange={(e) =>
                          handleFieldChange(
                            `eventRates.${idx}.volumeDiscount.discountedPrice`,
                            Number(e.target.value)
                          )
                        }
                        margin="dense"
                      />
                    </>
                  )}
                </Paper>
              </Grid>
            ))}
            {/* Contract Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  Contract Type
                </Typography>
                <RadioGroup
                  disabled={disabled}
                  row
                  value={form.contractType}
                  onChange={(e) =>
                    handleFieldChange("contractType", e.target.value)
                  }
                >
                  <FormControlLabel
                    disabled={disabled}
                    value="invoiced"
                    control={<Radio />}
                    label="Invoiced"
                  />
                  <FormControlLabel
                    disabled={disabled}
                    value="owned"
                    control={<Radio />}
                    label="Owned"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {/* Billing Cycle */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                  Billing Cycle
                </Typography>
                <RadioGroup
                  disabled={disabled}
                  row
                  value={form.billingCycle.type}
                  onChange={(e) =>
                    handleFieldChange("billingCycle.type", e.target.value)
                  }
                >
                  <FormControlLabel
                    disabled={disabled}
                    value="per_event"
                    control={<Radio />}
                    label="Per Event"
                  />
                  <FormControlLabel
                    disabled={disabled}
                    value="bi_monthly"
                    control={<Radio />}
                    label="Bi-Monthly"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {form.billingCycle.type === "per_event" && (
              <Grid item xs={12} md={4}>
                <TextField
                  disabled={disabled}
                  label="Days After Event Due"
                  type="number"
                  fullWidth
                  value={form.billingCycle.dueDays || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      "billingCycle.dueDays",
                      Number(e.target.value)
                    )
                  }
                  margin="dense"
                />
              </Grid>
            )}
            {form.billingCycle.type === "bi_monthly" && (
              <>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">Periods</Typography>
                  {form.billingCycle.periods.map((p, i) => (
                    <Grid container spacing={1} key={i}>
                      <Grid item xs={6}>
                        <TextField
                          disabled={disabled}
                          label="Start Day"
                          type="number"
                          fullWidth
                          value={p.startDay}
                          onChange={(e) =>
                            handleFieldChange(
                              `billingCycle.periods.${i}.startDay`,
                              Number(e.target.value)
                            )
                          }
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          disabled={disabled}
                          label="End Day"
                          type="number"
                          fullWidth
                          value={p.endDay}
                          onChange={(e) =>
                            handleFieldChange(
                              `billingCycle.periods.${i}.endDay`,
                              Number(e.target.value)
                            )
                          }
                          margin="dense"
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={disabled}
                    label="Days After Period Due"
                    type="number"
                    fullWidth
                    value={form.billingCycle.paymentDueDays || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "billingCycle.paymentDueDays",
                        Number(e.target.value)
                      )
                    }
                    margin="dense"
                  />
                </Grid>
              </>
            )}
            {/* Reset Period */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Reset Period</InputLabel>
                <Select
                  disabled={disabled}
                  value={form.resetPeriod}
                  label="Reset Period"
                  onChange={(e) =>
                    handleFieldChange("resetPeriod", e.target.value)
                  }
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: "bold", marginBottom: 10 }}
                >
                  Ticketing Fees
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Percent (%)"
                      type="number"
                      fullWidth
                      value={ticketFees?.percent ?? ""}
                      onChange={(e) =>
                        handleFeeChange("percent", Number(e.target.value))
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Fixed Fee (cents)"
                      type="number"
                      fullWidth
                      value={ticketFees?.fixedFeeCents ?? ""}
                      onChange={(e) =>
                        handleFeeChange("fixedFeeCents", Number(e.target.value))
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Processing %"
                      type="number"
                      fullWidth
                      value={ticketFees?.processingPercent ?? ""}
                      onChange={(e) =>
                        handleFeeChange(
                          "processingPercent",
                          Number(e.target.value)
                        )
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Processing Fixed ($)"
                      type="number"
                      fullWidth
                      value={ticketFees?.processingFixed ?? ""}
                      onChange={(e) =>
                        handleFeeChange(
                          "processingFixed",
                          Number(e.target.value)
                        )
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Currency"
                      fullWidth
                      value={ticketFees?.currency ?? ""}
                      onChange={(e) =>
                        handleFeeChange("currency", e.target.value)
                      }
                      margin="dense"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: "bold", marginBottom: 10 }}
                >
                  SMS Fees
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={disabled}
                          checked={!!smsFees?.enabled}
                          onChange={(e) => handleSmsChange("enabled", e.target.checked)}
                        />
                      }
                      label="Enabled"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Monthly Rate"
                      type="number"
                      fullWidth
                      value={smsFees?.monthlyRate ?? ""}
                      onChange={(e) =>
                        handleSmsChange("monthlyRate", Number(e.target.value))
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Included Messages"
                      type="number"
                      fullWidth
                      value={smsFees?.includedMessages ?? ""}
                      onChange={(e) =>
                        handleSmsChange("includedMessages", Number(e.target.value))
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Per Message Fee"
                      type="number"
                      fullWidth
                      value={smsFees?.perMessageFee ?? ""}
                      onChange={(e) =>
                        handleSmsChange("perMessageFee", Number(e.target.value))
                      }
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      size="small"
                      disabled={disabled}
                      label="Start Service Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={smsFees?.startService ?? ""}
                      onChange={(e) =>
                        handleSmsChange("startService", e.target.value)
                      }
                      margin="dense"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, backgroundColor: "#F0F0F5" }}>
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: "bold", marginBottom: 10 }}
                >
                  Payment Processing Fees (For Invoices)
                </Typography>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ fontWeight: "bold" }}
                >
                  ACH Fees
                </Typography>
                <Typography variant="body2">
                  ACH %:{" "}
                  {contract
                    ? (
                        contract.feePolicy?.feeAmounts?.achPercent * 100
                      ).toFixed(2)
                    : 0}
                  %
                </Typography>
                <Typography variant="body2">
                  ACH Fixed: $
                  {contract
                    ? contract.feePolicy?.feeAmounts?.achFixed.toFixed(2)
                    : 0}
                </Typography>
                <Typography variant="body2">
                  ACH Cap: $
                  {contract
                    ? contract.feePolicy?.feeAmounts?.achCap.toFixed(2)
                    : 0}
                </Typography>
                <Typography variant="body2">
                  Waive if Autopay:{" "}
                  {contract?.feePolicy?.ach?.waiveIfAutopay ? "Yes" : "No"}
                </Typography>
                <Typography variant="body2">
                  Waive if Paid Within (hours):{" "}
                  {contract?.feePolicy?.ach?.waiveIfPaidWithinHours ?? "N/A"}
                </Typography>

                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ mt: 2 }}
                  style={{ fontWeight: "bold" }}
                >
                  Credit Card Fees
                </Typography>
                <Typography variant="body2">
                  Credit Card %:{" "}
                  {contract
                    ? (
                        contract.feePolicy?.feeAmounts?.creditCardPercent * 100
                      ).toFixed(2)
                    : 0}
                  %
                </Typography>
                <Typography variant="body2">
                  Credit Card Fixed: $
                  {contract
                    ? contract.feePolicy?.feeAmounts?.creditCardFixed.toFixed(2)
                    : 0}
                </Typography>
                <Typography variant="body2">
                  Credit Card Cap: $
                  {contract
                    ? contract.feePolicy?.feeAmounts?.creditCardCap.toFixed(2)
                    : 0}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Typography>Loading...</Typography>
        )}
        <div style={{ height: 100 }} />
      </DialogContent>
    </Dialog>
  );
};

export default Contract;
